<template>
  <v-dialog v-model="visible" max-width="520" data-cy="connection-permissions-dialog">
    <v-card v-if="connection">
      <v-card-title>Connection permissions</v-card-title>

      <v-card-text>
        <div class="bg-grey-lighten-4 rounded p-3 mb-4">
          <div class="font-weight-medium">{{ connection.userName }}</div>
          <div class="text-caption text-grey-darken-1 mt-1">
            {{ connection.userEmail }} &middot; {{ connection.agentName }}
          </div>
          <div class="text-caption text-grey-darken-1">
            Connected {{ getShortDateString(connection.consentedAtUtc) }}
          </div>
        </div>

        <div class="text-caption text-grey-darken-1 mb-1">
          {{ firstName }} approved this agent for
        </div>
        <ul v-if="granted.length" class="ml-4" data-cy="granted-permissions">
          <li v-for="permission in granted" :key="permission.scope">
            {{ permission.label }}
            <span v-if="permission.requiresExplicitGrant && !isEffective(permission.scope)" class="text-caption text-grey-darken-1">
              &mdash; not allowed by your firm
            </span>
            <span v-else-if="permission.requiresExplicitGrant" class="text-caption text-amber-darken-4">
              &mdash; can send email
            </span>
            <span v-else-if="permission.isWrite" class="text-caption text-amber-darken-4">
              &mdash; can change data
            </span>
          </li>
        </ul>
        <p v-else class="text-grey-darken-1 mb-0" data-cy="no-granted-permissions">
          No permissions were approved on this connection.
        </p>

        <p class="text-caption text-grey-darken-1 mt-4 mb-0">
          To end this access, revoke the connection. Revoking cannot be undone &mdash; {{ firstName }}
          would have to connect again and approve it afresh.
        </p>
      </v-card-text>

      <v-card-actions class="p-6">
        <v-spacer />
        <LlSecondaryButton @click="visible = false">Close</LlSecondaryButton>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import LlSecondaryButton from '@/components/LlSecondaryButton.vue';
import { getShortDateString } from '@/services/utils';
import type { AgentAccessConnection, AgentAccessPermission } from '../models/agentAccessTypes';

const props = defineProps<{
  modelValue: boolean;
  connection: AgentAccessConnection | null;
  permissions: AgentAccessPermission[];
}>();

const emit = defineEmits<{ (e: 'update:modelValue', value: boolean): void }>();

const visible = computed({
  get: () => props.modelValue,
  set: (value: boolean) => emit('update:modelValue', value),
});

// A scope with no catalogue entry is still listed, by its raw name. Dropping it would under-report what
// an agent can reach, which is the one direction this screen must never err in.
const describe = (scope: string): AgentAccessPermission =>
  props.permissions.find((p) => p.scope === scope)
  ?? ({ id: '', scope, label: scope, description: '', isWrite: false, requiresExplicitGrant: false } as AgentAccessPermission);

const granted = computed(() => (props.connection?.grantedScopes ?? []).map(describe));

// What the person approved stays on record, but the firm's cap decides what still works. Only the
// permissions that have to be named are checked: a wildcard grant narrows to its parts, so it never
// matches effectiveScopes token for token.
const isEffective = (scope: string) => (props.connection?.effectiveScopes ?? []).includes(scope);

const firstName = computed(() => props.connection?.userName?.split(' ')[0] ?? 'They');
</script>
